import {
  boundsOfRects,
  createImage,
  createSticky,
  createText,
  detectEmbedProvider,
  createEmbed,
  nodeAABB,
  rectCenter,
  type BoardNode,
  type Vec,
} from '@kanvas/board-core';
import { ApiError, api, imageSize } from '../lib/api';
import { useEditor } from '../state/store';
import { cloneNodes, expandForClone } from './clone';
import { reparentIntoFrames, type ActionContext } from './actions';

const MIME = 'application/x-kanvas-nodes';
/** Prefix used when smuggling the payload through text/plain. */
const TEXT_MARKER = '__kanvas_clipboard__:';

export interface ClipboardPayload {
  kind: 'kanvas/nodes';
  version: 1;
  nodes: BoardNode[];
  /** Bounds at copy time, so paste can centre the group under the pointer. */
  bounds: { x: number; y: number; w: number; h: number };
}

/**
 * In-process fallback.
 *
 * Reading the system clipboard needs a permission the user may decline, and
 * Firefox still gates `clipboard.read()` entirely. Keeping the last copy in
 * memory means Ctrl+C / Ctrl+V always works inside the app regardless.
 */
let memoryClipboard: ClipboardPayload | null = null;

function buildPayload(ctx: ActionContext): ClipboardPayload | null {
  const ids = useEditor.getState().selection;
  if (ids.length === 0) return null;
  const nodes = expandForClone(ids, ctx.doc.all(), ctx.doc.lookup);
  if (nodes.length === 0) return null;
  const bounds = boundsOfRects(nodes.map((n) => nodeAABB(n, ctx.doc.lookup)));
  return { kind: 'kanvas/nodes', version: 1, nodes, bounds };
}

/** What a copy of `payload` puts on the clipboard as plain text. */
function plainTextOf(payload: ClipboardPayload): string {
  return payload.nodes
    .map((n) => ('text' in n ? (n as { text: string }).text : ''))
    .filter((t) => t !== '')
    .join('\n');
}

/**
 * The board objects on the clipboard, if what is there is a copy from a
 * board: the custom type, the text-marker fallback, or — when the browser
 * kept only plain text — text identical to the last in-app copy.
 */
export function readBoardClipboard(data: DataTransfer | null | undefined): ClipboardPayload | null {
  const parse = (raw: string): ClipboardPayload | null => {
    try {
      const payload = JSON.parse(raw) as ClipboardPayload;
      return payload?.kind === 'kanvas/nodes' && Array.isArray(payload.nodes) ? payload : null;
    } catch {
      return null;
    }
  };
  const custom = data?.getData(MIME);
  if (custom) return parse(custom);
  const text = data?.getData('text/plain') ?? '';
  if (text.startsWith(TEXT_MARKER)) return parse(text.slice(TEXT_MARKER.length));
  if (memoryClipboard && text !== '' && text === plainTextOf(memoryClipboard)) return memoryClipboard;
  return null;
}

export async function copySelection(ctx: ActionContext, event?: ClipboardEvent): Promise<void> {
  const payload = buildPayload(ctx);
  if (!payload) return;
  memoryClipboard = payload;

  const json = JSON.stringify(payload);
  const plain = payload.nodes
    .map((n) => ('text' in n ? (n as { text: string }).text : ''))
    .filter((t) => t !== '')
    .join('\n');

  const text = plain !== '' ? plain : `${TEXT_MARKER}${json}`;

  // Inside a copy/cut event the clipboard can be written synchronously, with
  // the custom type included — the async API rejects custom types in Chrome.
  if (event?.clipboardData) {
    event.clipboardData.setData(MIME, json);
    event.clipboardData.setData('text/plain', text);
    return;
  }

  try {
    if ('ClipboardItem' in window && navigator.clipboard?.write) {
      await navigator.clipboard.write([
        new ClipboardItem({
          'text/plain': new Blob([plain !== '' ? plain : `${TEXT_MARKER}${json}`], {
            type: 'text/plain',
          }),
          // A custom type keeps a round-trip lossless when the browser allows it.
          [MIME]: new Blob([json], { type: MIME }),
        } as unknown as Record<string, Blob>),
      ]);
    } else {
      await navigator.clipboard?.writeText(text);
    }
  } catch {
    // Custom types are refused by some browsers; plain text still round-trips
    // through TEXT_MARKER. If even that is denied, the in-memory copy serves
    // paste inside the app.
    try {
      await navigator.clipboard?.writeText(`${TEXT_MARKER}${json}`);
    } catch {
      // Clipboard denied.
    }
  }
}

export async function cutSelection(ctx: ActionContext, event?: ClipboardEvent): Promise<void> {
  await copySelection(ctx, event);
  const ids = useEditor.getState().selection.filter((id) => !ctx.doc.get(id)?.locked);
  if (ids.length > 0) {
    ctx.doc.remove(ids);
    useEditor.getState().clearSelection();
  }
}

/**
 * Pastes whatever the clipboard holds.
 *
 * Priority mirrors what a user expects: our own nodes first, then image files,
 * then a URL (embed), then plain text (sticky note).
 */
export async function paste(
  ctx: ActionContext,
  at: Vec,
  event?: ClipboardEvent,
): Promise<void> {
  const dt = event?.clipboardData;

  if (dt) {
    const custom = dt.getData(MIME);
    if (custom) {
      pasteNodes(ctx, JSON.parse(custom) as ClipboardPayload, at);
      return;
    }
    const files = [...dt.files];
    const image = files.find((f) => f.type.startsWith('image/'));
    if (image) {
      await pasteImageFile(ctx, image, at);
      return;
    }
    const text = dt.getData('text/plain');
    if (text) {
      await pasteText(ctx, text, at);
      return;
    }
  }

  // Keyboard-driven paste without a ClipboardEvent: ask the async API.
  try {
    if (navigator.clipboard?.read) {
      const items = await navigator.clipboard.read();
      for (const item of items) {
        if (item.types.includes(MIME)) {
          const blob = await item.getType(MIME);
          pasteNodes(ctx, JSON.parse(await blob.text()) as ClipboardPayload, at);
          return;
        }
        const imageType = item.types.find((t) => t.startsWith('image/'));
        if (imageType) {
          const blob = await item.getType(imageType);
          await pasteImageFile(ctx, new File([blob], 'pasted', { type: imageType }), at);
          return;
        }
      }
    }
    const text = await navigator.clipboard?.readText();
    if (text) {
      await pasteText(ctx, text, at);
      return;
    }
  } catch {
    // Fall through to the in-memory copy.
  }

  if (memoryClipboard) pasteNodes(ctx, memoryClipboard, at);
}

function pasteNodes(ctx: ActionContext, payload: ClipboardPayload, at: Vec): void {
  if (payload.kind !== 'kanvas/nodes' || payload.nodes.length === 0) return;

  const center = rectCenter(payload.bounds);
  const offset: Vec = { x: at.x - center.x, y: at.y - center.y };
  const indices = ctx.doc.topIndices(payload.nodes.length);
  const { nodes, idMap } = cloneNodes(payload.nodes, {
    offset,
    indices,
    userId: ctx.identity.userId,
  });
  ctx.doc.addMany(nodes);
  reparentIntoFrames(ctx, nodes.map((n) => n.id));
  // Select the roots only: a member alongside its group would give the drag
  // and the style panel two handles on the same object.
  const pasted = new Set(payload.nodes.map((n) => n.id));
  const roots = payload.nodes.filter((n) => {
    const parent = n.parentId ? payload.nodes.find((p) => p.id === n.parentId) : undefined;
    return !(parent && parent.type === 'group' && pasted.has(parent.id));
  });
  useEditor.getState().select(roots.map((n) => idMap.get(n.id)!));
  useEditor.getState().setTool('select');
}

/** Why an image upload failed, in words the person dropping the file can act on. */
function uploadErrorText(error: unknown, file: File): string {
  const name = file.name ? `«${file.name}»` : 'Картинку';
  if (error instanceof ApiError) {
    if (error.status === 413) return `${name} не удалось загрузить: файл больше 25 МБ`;
    if (error.status === 415) return `${name} не удалось загрузить: этот формат не поддерживается`;
    if (error.status === 401) return 'Сессия истекла — войдите заново, чтобы загружать картинки';
  }
  return `${name} не удалось загрузить. Проверьте соединение и попробуйте ещё раз`;
}

/** Uploads an image and drops it on the board. False when nothing was added. */
export async function pasteImageFile(
  ctx: ActionContext,
  file: File,
  at: Vec,
): Promise<boolean> {
  // Don't upload for someone who can't place the result.
  if (!ctx.doc.canEdit()) return false;
  let asset;
  try {
    asset = await api.uploadAsset(file);
  } catch (error) {
    // A failed upload used to vanish as an unhandled rejection — and, in a
    // multi-file drop, take the remaining files with it.
    useEditor.getState().notify(uploadErrorText(error, file));
    return false;
  }
  const node = createImage(
    at.x,
    at.y,
    asset.url,
    asset.width,
    asset.height,
    { userId: ctx.identity.userId, index: ctx.doc.topIndex() },
  );
  // Centre it on the drop point.
  node.x -= node.w / 2;
  node.y -= node.h / 2;
  ctx.doc.add(node);
  reparentIntoFrames(ctx, [node.id]);
  useEditor.getState().select([node.id]);
  return true;
}

async function pasteText(ctx: ActionContext, raw: string, at: Vec): Promise<void> {
  if (raw.startsWith(TEXT_MARKER)) {
    pasteNodes(ctx, JSON.parse(raw.slice(TEXT_MARKER.length)) as ClipboardPayload, at);
    return;
  }

  const text = raw.trim();
  const isUrl = /^https?:\/\/\S+$/i.test(text);

  if (isUrl) {
    // Direct links to an image become an image; anything else becomes an embed.
    if (/\.(png|jpe?g|gif|webp|avif|svg)(\?|$)/i.test(text)) {
      const size = await imageSize(text);
      const node = createImage(at.x, at.y, text, size.width, size.height, {
        userId: ctx.identity.userId,
        index: ctx.doc.topIndex(),
      });
      node.x -= node.w / 2;
      node.y -= node.h / 2;
      ctx.doc.add(node);
      reparentIntoFrames(ctx, [node.id]);
      useEditor.getState().select([node.id]);
      return;
    }
    const provider = detectEmbedProvider(text);
    if (provider !== 'iframe') {
      const node = createEmbed(at.x - 320, at.y - 180, text, {
        userId: ctx.identity.userId,
        index: ctx.doc.topIndex(),
      });
      ctx.doc.add(node);
      useEditor.getState().select([node.id]);
      return;
    }
  }

  const lines = text.split('\n').filter((l) => l.trim() !== '');
  const editor = useEditor.getState();

  // A short single line becomes a sticky; long or multi-line text becomes a
  // text block, which is what people mean when they paste a paragraph.
  if (lines.length === 1 && text.length <= 180) {
    const node = createSticky(at.x - 90, at.y - 90, {
      userId: ctx.identity.userId,
      index: ctx.doc.topIndex(),
    }, { fill: editor.stickyFill, text });
    ctx.doc.add(node);
    editor.select([node.id]);
    return;
  }

  if (lines.length > 1 && lines.length <= 40 && lines.every((l) => l.length <= 120)) {
    // Multiple short lines: one sticky per line, laid out in a grid.
    const indices = ctx.doc.topIndices(lines.length);
    const perRow = Math.ceil(Math.sqrt(lines.length));
    const nodes = lines.map((line, i) =>
      createSticky(
        at.x + (i % perRow) * 200 - (perRow * 200) / 2,
        at.y + Math.floor(i / perRow) * 200 - 90,
        { userId: ctx.identity.userId, index: indices[i]! },
        { fill: editor.stickyFill, text: line },
      ),
    );
    ctx.doc.addMany(nodes);
    editor.select(nodes.map((n) => n.id));
    return;
  }

  const node = createText(at.x - 200, at.y - 20, {
    userId: ctx.identity.userId,
    index: ctx.doc.topIndex(),
  }, { text, w: 400 });
  ctx.doc.add(node);
  editor.select([node.id]);
}

/** Handles files dropped onto the canvas. */
export async function dropFiles(
  ctx: ActionContext,
  files: readonly File[],
  at: Vec,
): Promise<void> {
  let offset = 0;
  for (const file of files) {
    if (file.type.startsWith('image/')) {
      await pasteImageFile(ctx, file, { x: at.x + offset, y: at.y + offset });
      offset += 28;
      continue;
    }
    if (file.type === 'application/json' || file.name.endsWith('.json')) {
      try {
        const payload = JSON.parse(await file.text()) as { nodes?: BoardNode[] };
        if (Array.isArray(payload.nodes)) {
          ctx.doc.importJSON({ nodes: payload.nodes });
          continue;
        }
      } catch {
        // Not a board export; fall through to the text handling below.
      }
    }
    if (file.type.startsWith('text/')) {
      await pasteText(ctx, await file.text(), { x: at.x + offset, y: at.y + offset });
      offset += 28;
    }
  }
}
